import { addListener, getResultFromResponse, invoke, loop, retryWithFibonacci, runAsyncSafe } from '@web3/utils';
import { useMemo } from 'react';
import { getWalletEvmProvider, userInfoStore } from '../../entry_inner';
import { AddressGroupByChainType, getChainBalanceListAPI } from '../api';
import { WalletChainBalance, WalletTypesNumber } from '../types';
import { walletIdMap } from '../utils/constants';
import { EventBasedStore, GetStoreState, pickStoreActions } from '../utils/EventBasedStore';
import { monitorLog } from '../utils/monitor';
import { oneRequestForSameParam } from '../utils/oneRequestForSameParam';
import { innerUseNativeTokenDecimal } from '../utils/trick';
import { ConnectOption } from '../wallet-hub/core/WalletHub';
import { bybitExtension, isRequireBybitExtensionUpdate } from '../wallet-hub/hubs/BybitExtensionHub';
import { hexNumberString } from '../wallet-hub/misc';
import { thirdPartyWallet } from '../wallet-hub/Web3WalletHubCenter';
import { cleanLastRun, debugLog, dependencyContainer, getRpcUrl } from './../utils/common';
import { chainListStore } from './chainList';
import { getWeb3InfraGlobalConfig } from './config';
import { connectWalletModalStore } from './dialogs';
import { debounce, flatten } from 'lodash-es';
import { ChainTypes } from '../wallet-hub/core/types';
import { BtcAddressItem, BtcChainCode } from '../types/BtcAddress';

type State = {
  walletIsReady: boolean;
  totalBalance: string;
  chainBalanceList: WalletChainBalance[];
};

const addressesList = {
  btcAddress: '',
  // btcAddress 对应的类型
  btcAddressType: '',
  evmAddress: '',
  solAddress: '',
  suiAddress: '',
  stxAddress: '',
  tonAddress: '',
  tronAddress: '',
  aptAddress: '',
  btcAddressList: [] as BtcAddressItem[],
};

const computeAddress = (state: State): typeof addressesList => {
  const isNone = !thirdPartyWallet.activeWalletHub;
  if (!state.walletIsReady || isNone) {
    return {
      ...addressesList,
    };
  }
  const wallets = thirdPartyWallet.wallets;

  /** 这里先处理Bybit Wallet 的BTC 地址列表，未来如果有支持别的btc钱包，再在这里处理 */
  const btcAddressList: BtcAddressItem[] = !thirdPartyWallet.isBybitWalletExtension
    ? []
    : bybitExtension.walletInfo?.addressChain
        .filter((a) => a.chainType === ChainTypes.BTC)
        .map((a) => {
          return {
            address: a.address,
            chainCode: a.chainCode,
            chainId: a.chainId,
            publicKey: a.publicKey,
          };
        }) ?? [];
  return {
    btcAddress: wallets?.BTC?.address ?? '',
    btcAddressType: wallets?.BTC?.connector?.addressType ?? '',
    evmAddress: wallets?.EVM?.address ?? '',
    solAddress: wallets?.SOL?.address ?? '',
    suiAddress: wallets?.SUI?.address ?? '',
    stxAddress: wallets?.STX?.address ?? '',
    tonAddress: wallets?.TON?.address ?? '',
    tronAddress: wallets?.TRON?.address ?? '',
    // 兼容从bybit walletInfo 拿apt地址
    aptAddress: bybitExtension.walletInfo?.addressChain.find((a) => a.chainType === ChainTypes.APT)?.address ?? '',
    btcAddressList,
  };
};

const compute = (state: State) => {
  const addresses = computeAddress(state);
  const isNone = !thirdPartyWallet.activeWalletHub;

  const computed = {
    // 是否是浏览器钱包
    isBrowserWallet: false,
    // 是否是bybit 云钱包（如果是插件代表插件内用户选的云钱包）
    isCloudWallet: !isNone && thirdPartyWallet.isBybitWalletExtension && bybitExtension.isBybitCloud,
    // 是否选的三方钱包
    isThirdParty: !isNone,
    // 是否是没选钱包
    isNone,
    ...addresses,
    evmChainId: thirdPartyWallet.activeWalletHub?.evmChainId ?? 0,
  };

  const isBybitExtension = thirdPartyWallet.isBybitWalletExtension;

  // 当前钱包类型：云钱包、bybit插件里的助记词类型、其它都算第三方
  const walletType = invoke(() => {
    if (isNone) {
      return WalletTypesNumber.NOT;
    }
    return thirdPartyWallet.isBybitWalletExtension ? bybitExtension.walletType : WalletTypesNumber.THIRD_PARTY;
  });

  // 钱包id
  const walletId: string = invoke(() => {
    if (isNone) {
      return '';
    }
    return thirdPartyWallet.isBybitWalletExtension ? bybitExtension.walletInfo?.walletId ?? '' : '';
  });

  return {
    ...computed,
    ...addresses,
    walletType,
    walletId,
    isBybitExtension,
  };
};

type Computed = ReturnType<typeof compute>;

class UserWalletStore extends EventBasedStore<State, Computed> {
  constructor() {
    super(
      {
        walletIsReady: false,
        totalBalance: '0',
        chainBalanceList: [],
      },
      compute,
    );
  }

  private _tokenBalanceDeps = dependencyContainer();
  async getAllBalance(clearWhenFail = false, force = false) {
    const state = this.state;
    const { isNone, walletId, walletType } = state;
    const addressInfos = Object.keys(addressesList).map((key) => {
      const v = state[key as keyof typeof addressesList];
      if (typeof v === 'string' || typeof v === 'number') {
        return v;
      }
      return JSON.stringify(v);
    });
    const deps = [isNone, walletType, walletId].concat(addressInfos);

    const res = this._tokenBalanceDeps.checkAndRun(
      async () => {
        if (isNone) {
          this.setState({
            totalBalance: '',
            chainBalanceList: [],
          });
          return;
        }

        const addressChain = await this.getAddressChain();

        const [err, res] = await runAsyncSafe(
          getResultFromResponse(
            getChainBalanceListAPI({
              walletId,
              type: walletType,
              chainAddress: addressChain.reduce((acc, c) => {
                const addressItem = acc.find((a) => a.address === c.address);
                if (!addressItem) {
                  acc.push({
                    address: c.address,
                    chainIds: [Number(c.chainId)],
                  });
                } else {
                  addressItem.chainIds.push(Number(c.chainId));
                }
                return acc;
              }, [] as AddressGroupByChainType[]),
            }),
          ),
        );
        if (res) {
          this.setState({
            totalBalance: res.totalAmountUsd,
            chainBalanceList: mergeBtcBalance(res.addressAsset),
          });
          return;
        }
        if (clearWhenFail) {
          this.setState({
            totalBalance: '',
            chainBalanceList: [],
          });
        }
      },
      deps,
      force,
    );
    return res.result;
  }

  async getAddressChain() {
    const { walletType } = this.state;
    await chainListStore.getChainList();
    const chains = walletType === WalletTypesNumber.CLOUD ? chainListStore.state.chainList : chainListStore.state.mnemonicChainList;
    const addressChain = chains
      .filter((c) => !c.chainCode.includes('TESTNET'))
      .map((c) => ({
        ...c,
        address: this.getAddressByType(c.chainType as number as ChainTypes, c.chainCode) ?? '',
      }))
      .filter((c) => c.address);
    return addressChain;
  }

  getBtcAddressChain() {
    if (this.state.isBybitExtension && bybitExtension.walletInfo) {
      return bybitExtension.walletInfo.addressChain.filter((c) => c.chainType === ChainTypes.BTC);
    }
  }

  getToken(chainCode?: string, info?: string) {
    if (!chainCode || !info) {
      return;
    }
    const chain = chainListStore.getChain(chainCode);

    return this.state.chainBalanceList
      .find((b) => b.chainId === chain?.chainId)
      ?.tokenAsset.find((t) => info.toUpperCase() === t.symbol.toUpperCase());
  }

  /**
   * 根据chainCode 获取钱包地址（没匹配到就为evm地址）
   */
  getAddress(chainCode?: string) {
    const state = this.state;
    if (!chainCode) {
      return '';
    }
    const { btcAddressList } = state;
    switch (chainCode) {
      case 'SOL':
        return state.solAddress;
      case 'SUI':
        return state.suiAddress;
      case 'STX':
        return state.stxAddress;
      case 'TON':
        return state.tonAddress;
      case 'TRON':
        return state.tronAddress;
      case 'APT':
        return state.aptAddress;
      default:
        return invoke(() => {
          if (chainCode.includes('BTC')) {
            return btcAddressList.find((a) => a.chainCode === chainCode)?.address ?? '';
          }
          if (state.isBybitExtension) {
            // bybit 插件，可动态从wallet Info拿地址， 这样可以动态支持 插件支持到的链
            const address = bybitExtension.walletInfo?.addressChain.find((a) => a.chainCode === chainCode)?.address ?? '';
            if (address) {
              return address;
            }
          }
          return state.evmAddress || '';
        });
    }
  }
  /** 根据chainType 获取地址， btc 还可以指定地址类型 */
  getAddressByType(type: ChainTypes, addressType?: string) {
    const state = this.state;
    switch (type) {
      case ChainTypes.EVM:
        return state.evmAddress;
      case ChainTypes.SOL:
        return state.solAddress;
      case ChainTypes.BTC:
        return state.btcAddressList.find((a) => a.chainCode === addressType)?.address ?? state.btcAddress ?? '';
      case ChainTypes.SUI:
        return state.suiAddress;
      case ChainTypes.STX:
        return state.stxAddress;
      case ChainTypes.TON:
        return state.tonAddress;
      case ChainTypes.APT:
        return state.aptAddress;
      case ChainTypes.TRON:
        return state.tronAddress;
      default:
        return invoke(() => {
          // 如果bybit 插件动态从 address Info 里去拿某个链类型地址，这样可以动态支持 插件支持到的链
          if (state.isBybitExtension) {
            const address = bybitExtension.walletInfo?.addressChain.find((a) => a.chainType === type)?.address ?? '';
            if (address) {
              return address;
            }
          }
          return '';
        });
    }
  }

  /**
   *
   * @param walletId 钱包Id，Browser 是特殊的钱包ID，是切到浏览器云钱包
   */
  async switchToWallet(walletId: string, opt?: ConnectOption) {
    if (walletIdMap.bybit === walletId && isRequireBybitExtensionUpdate()) {
      // 因为一般都调用 switchToBybitExtension 来连接bybit插件，这里就直接做一个报错就好了，连接弹窗页面是会判断插件是否够版本的
      throw new Error('bybit extension update required');
    }
    await thirdPartyWallet.switch(walletId, false, opt);
    if (thirdPartyWallet.isEmpty) {
      throw new Error('wallet connect error');
    }
  }
  /**
   * 切换到bybit插件，如果open为true，则打开插件
   * @param walletType 钱包类型
   * @param open 是否打开插件, 默认是true
   */
  async switchToBybitExtension(
    walletType?: WalletTypesNumber,
    opt?: {
      extVersion?: string;
    },
  ) {
    const { isBybitExtension, walletType: currentWalletType } = this.state;
    const requireUpdate = isRequireBybitExtensionUpdate(opt?.extVersion);

    const modalConnectBybit = () => {
      return new Promise((resolve, reject) => {
        connectWalletModalStore.show({
          bybitExtVersion: opt?.extVersion,
          onConnected: () => {
            resolve(undefined);
          },
          onClose: () => {
            reject(new Error('user cancel'));
          },
        });
      });
    };

    if (!isBybitExtension) {
      if (!(await bybitExtension.isAvailable)) {
        // 没有bybit插件，弹出连接弹窗,里面会提示 下载
        return modalConnectBybit();
      }
      // 有bybit插件，但是版本需要更新
      if (requireUpdate) {
        return modalConnectBybit();
      }
      // 连接的不是bybit插件，切换到bybit插件
      return this.switchToWallet(
        walletIdMap.bybit,
        walletType
          ? {
              EVM: {
                args: [
                  {
                    walletType: [walletType],
                  },
                ],
              },
            }
          : undefined,
      );
    }

    if (walletType && currentWalletType !== walletType) {
      if (requireUpdate) {
        return modalConnectBybit();
      }
      // 连接的是bybit插件，切换到bybit插件的指定钱包
      const provider = await bybitExtension.EVM?.getProvider();
      return provider?.request({
        method: 'switchWallet',
        params: [
          {
            walletType: [walletType],
            chainType: 1,
          },
        ],
      });
    }
    // 其它情况不无需处理
  }

  /**
   * 切换到云钱包，如果未登录会弹登录，没有创钱包，就会弹出创建提示
   *
   * @deprecated 后续会移除该方法，开始逐步下限网页内云钱包，后续会彻底移除，现在给改为直接弹出连接钱包
   */
  switchToBrowser() {
    connectWalletModalStore.show();
  }
  /**  
  // 切换到三方钱包（如果有连接的话），没有的话，就会弹出连接弹窗
   *  @deprecated 后续会移除该方法，没有了网页云钱包，也就没有这个对立的概念了
  */
  switchToThirdParty() {
    if (this.state.isThirdParty) {
      return;
    }
    connectWalletModalStore.show();
  }

  /**
   * 连接状态直接和 thirdPartyWallet 的 isEmpty 状态一致，所以就等于是 断开连接
   */
  switchToNone() {
    thirdPartyWallet.dispose();
  }

  async switchEvmChain(chainId: number) {
    if (this.state.isThirdParty) {
      const chain = chainListStore.getChain(chainId);
      return thirdPartyWallet.activeWalletHub?.EVM?.switchChain(
        chainId,
        chain
          ? {
              chainId: hexNumberString(chain.chainId),
              chainName: chain.chainName,
              rpcUrls: [getRpcUrl(chain.chainCode)],
              nativeCurrency: {
                name: chain.chainName,
                decimals: innerUseNativeTokenDecimal,
                symbol: chain.baseTokenName,
              },
              blockExplorerUrls: [chain.txAddress],
              iconUrls: [chain.iconNightUrl, chain.iconDayUrl],
            }
          : undefined,
      );
    }
  }

  // 获取evm provider，包括兼容的 bybit evm 钱包 Signer
  getEvmSigner() {
    if (this.state.isThirdParty) {
      return getWalletEvmProvider()?.getSigner();
    }
    return;
  }
}

export const userWalletStore = new UserWalletStore();

export type UserWalletStoreState = GetStoreState<UserWalletStore>;

export const useCurrentWallet = () => {
  const state = userWalletStore.useStoreState();
  const actions = useMemo(
    () =>
      pickStoreActions(userWalletStore, [
        'switchToWallet',
        'switchToBybitExtension',
        'switchToBrowser',
        'switchToThirdParty',
        'getAllBalance',
        'getToken',
        'getAddress',
        'getAddressByType',
        'switchToNone',
        'switchEvmChain',
        'getEvmSigner',
      ]),
    [],
  );
  return {
    ...state,
    ...actions,
  };
};

/**
 * 初始化钱包的方法
 */
export const initStore = async () => {
  const store = userWalletStore;
  await thirdPartyWallet.ready;
  debugLog('wallet is ready.....');
  store.setState({
    walletIsReady: true,
  });

  const { autoUpdateBalance } = getWeb3InfraGlobalConfig();

  //   监听三方钱包变化
  addListener(thirdPartyWallet, 'walletChange', () => {
    // 触发状态更新即可
    store.setState({});
  });

  if (autoUpdateBalance) {
    // 会自动在每次update的时候都清理 loop的定时器
    store.on(
      'update',
      debounce(
        cleanLastRun(() => {
          store.getAllBalance(true);
          return loop(
            () => {
              store.getAllBalance(false, true);
            },
            {
              interval: 60e3,
              immediate: false,
            },
          );
        }),
        100,
      ),
    );
  }

  const checkCloudWalletLogin = oneRequestForSameParam(async () => {
    const state = store.state;
    const [err] = await runAsyncSafe(
      retryWithFibonacci(async () => {
        await userInfoStore.getUserInfo();
        if (!userInfoStore.state.userInfo) {
          throw new Error('check cloud wallet login error');
        }
      }, 3),
    );
    if (err) {
      // 连接了bybit 云钱包，但是没有登录，做一个事件上报，这是一个很大的异常；
      // 并给切换到没有钱包
      store.switchToNone();
      monitorLog({
        t: 'bybit_cloud_wallet_no_login',
        walletId: state.walletId,
        walletType: state.walletType,
        error: userInfoStore.state.error,
      });
    }
  });

  store.on('update', (state) => {
    if (state.walletType === WalletTypesNumber.CLOUD && !userInfoStore.state.userInfo && !userInfoStore.state.loading) {
      checkCloudWalletLogin();
    }
  });
};

/** 合并BTC 资产到 native 链上 */
const mergeBtcBalance = (assets: WalletChainBalance[]) => {
  const btcAssets = assets.filter((a) => a.chainType === ChainTypes.BTC);
  const otherAssets = assets.filter((a) => a.chainType !== ChainTypes.BTC);
  if (!btcAssets.length) {
    return assets;
  }
  const nativeBtc = btcAssets.find((a) => a.chainCode === BtcChainCode.NATIVE) || btcAssets[0];
  nativeBtc.tokenAsset = flatten(btcAssets.map((a) => a.tokenAsset)).sort((a, b) => Number(b.chainId) - Number(a.chainId));
  return [nativeBtc, ...otherAssets];
};
